import { PRESETS, PERMISSIONS_BY_PRESET } from '@wix/wix-code-collections-api'
import { getPagesStepsCreator } from './pagesSteps'
import { bindComponents } from './componentBindingStep'
import { getCollectionStepTitle, getPageStepTitle } from './progressBarTitles'
import { getEventsLogger } from './bi/getEventsLogger'
import {
  createAndPopulatePresetCollection,
  createPageLinkFields,
  getCollectionType,
  runTransactionWithRetry,
  UNDO_REDO_LABELS,
} from '@wix/wix-data-client-common'
import { uniq } from 'lodash'
import {
  nonNullable,
  type EditorSDK,
  type I18n,
  type Logger,
} from '@wix/wix-data-client-common-standalone'
import type { CollectionsApi, Patch } from '@wix/wix-code-collections-api'
import type {
  AppApi,
  DpBiDefaults,
  EDITOR_TYPE,
  PagesToAdd,
  PresetDefinition,
} from '@wix/wix-data-client-common'
import type Experiments from '@wix/wix-experiments'
import type { HttpClient } from '@wix/http-client'
import type { ProgressBar } from '../../progress-bar/executeSteps'
import type { Schema } from '@wix/wix-data-schema-types'
import type { WixDataApi } from '@wix/wix-data-core'
import type { CreateOrConnectDPState } from '../../../types'
import type { CollectionToAdd } from './flows/conversion/prepareDataForCollection'
import { DATA_BINDING } from '@wix/app-definition-ids'

const collectionType = PRESETS.CONTENT_FOR_YOUR_SITE
const permissions = PERMISSIONS_BY_PRESET[collectionType]
const orderPages = ({
  preset,
  pagesToAdd,
}: {
  preset: PresetDefinition
  pagesToAdd: PagesToAdd
}) =>
  preset.pages
    .map(page => pagesToAdd.find(pageToAdd => pageToAdd.key === page.type))
    .filter(nonNullable)

interface PageLinkFieldConfig {
  displayName: string
  pageId: string
  routerPrefix: string
  fieldKey: string
  pattern: string
}

export const afterAddingPages = async ({
  editorSdkProxy,
  collectionsApi,
  collectionId,
  pageLinkFieldsToAdd,
}: {
  editorSdkProxy: EditorSDK
  collectionsApi: CollectionsApi
  collectionId: string
  pageLinkFieldsToAdd: PageLinkFieldConfig[]
}) => {
  await Promise.all([
    editorSdkProxy.document.application.reloadManifest(),

    createPageLinkFields({
      collectionsApi,
      collectionId,
      fieldsData: pageLinkFieldsToAdd.map(
        ({ fieldKey, pattern, routerPrefix, displayName, pageId }) => ({
          key: fieldKey,
          displayName,
          prefix: routerPrefix,
          pattern,
          pageId,
        }),
      ),
    }),
  ])
}

const initState = (): CreateOrConnectDPState => ({
  pages: [],
  pageDefinitions: {},
  routerPrefix: null,
  managingAppDefId: DATA_BINDING,
  installedAppIds: new Map(),
})

export const getDpStepBuilder = ({
  collectionsApi,
  editorSdkProxy,
  wixData,
  i18n,
  editorType,
  isSandboxEnabled,
  defaultDraftPublishStatus,
  pluginsPatches,
  appApi,
  logger,
  biDefaults,
  experimentsManager,
  httpClient,
}: {
  collectionsApi: CollectionsApi
  editorSdkProxy: EditorSDK
  wixData: WixDataApi
  i18n: I18n
  editorType: EDITOR_TYPE
  isSandboxEnabled: boolean
  defaultDraftPublishStatus: string
  pluginsPatches: Patch[]
  appApi: AppApi
  logger: Logger
  biDefaults: DpBiDefaults
  experimentsManager: Experiments
  httpClient: HttpClient
}) => {
  const getPagesSteps = getPagesStepsCreator({
    appApi,
    editorType,
    editorSdkProxy,
    experimentsManager,
    logger,
    httpClient,
  })

  const biEvents = getEventsLogger({
    isSandboxEnabled,
    defaultDraftPublishStatus,
    logger,
    biDefaults,
  })

  return {
    getStepsForAddingPagesFromPreset: async ({
      collectionId,
      collectionName,
      preset,
      pagesToAdd,
      isIndexable,
      progressBar,
    }: {
      collectionId: string
      collectionName: string
      preset: PresetDefinition
      pagesToAdd: PagesToAdd
      isIndexable: boolean
      progressBar: ProgressBar
    }) => {
      let startTime = 0

      const initStartTimeToCalculatePresetInstallationDuration = () => {
        startTime = Date.now()
      }

      const { schema, records } = preset.collections[0]

      const state = initState()

      const orderedPages = orderPages({ preset, pagesToAdd })

      const pagesSteps = getPagesSteps({
        orderedPages,
        collectionId,
        collectionName,
        preset,
        isFromExistingCollection: false,
        isIndexable,
        schema,
      })

      await progressBar.open()

      try {
        await progressBar.update({
          stepTitle: getCollectionStepTitle({
            i18n,
            pagesToAdd,
            collectionName,
          }),
        })
        initStartTimeToCalculatePresetInstallationDuration()
        await createAndPopulatePresetCollection({
          collectionsApi,
          collectionId,
          collectionName,
          wixDataApi: wixData,
          schema,
          records,
          pluginsPatches,
        })

        biEvents.logForPresetCollection({
          preset,
          collectionId,
          collectionName,
          collectionType,
          numberOfRows: records.length,
          numberOfFields: Object.keys(schema.fields).length,
        })

        // would it make sense to install apps before pages steps?
        if (pagesToAdd.length > 0) {
          await runTransactionWithRetry(editorSdkProxy, async () => {
            for (const [i, pageStep] of pagesSteps.entries()) {
              const { key, staticPageRef } = orderedPages[i]
              const pageStepTitle = getPageStepTitle({
                i18n,
                pageKey: key,
                staticPageRef,
              })
              try {
                await progressBar.update({ stepTitle: pageStepTitle })
                await pageStep.action({ state })
              } catch (error) {
                const wrappedError = new Error('Failed to add preset page', {
                  cause: error,
                })
                logger.error(wrappedError, {
                  pageKey: key,
                  collectionId,
                  editorType,
                })
                throw wrappedError
              }
            }
          })

          await afterAddingPages({
            editorSdkProxy,
            collectionsApi,
            collectionId,
            pageLinkFieldsToAdd: state.pages.map(page => ({
              fieldKey: state.pageDefinitions[page.key].pageLinkFieldKey,
              pattern: state.pageDefinitions[page.key].pattern,
              routerPrefix: state.routerPrefix!,
              displayName: page.pageTitle,
              pageId: page.pageRef.id,
            })),
          })

          await editorSdkProxy.history.add({
            label: UNDO_REDO_LABELS.ADD_DATA_BINDING_URL,
          })
        }

        biEvents.logForAddedPreset({
          state,
          preset,
          collectionId,
          collectionName,
          startTime,
        })

        await progressBar.update()
      } finally {
        await progressBar.close()
      }

      return {
        pageRefs: state.pages.map(p => p.pageRef),
        pages: state.pages.map(p => ({
          pageRef: p.pageRef,
          pageType: p.pageType,
        })),
      }
    },
    getStepsForAddingDefaultPagesFromExistingCollection: async ({
      preset,
      pagesToAdd,
      collectionId,
      collectionName,
      isIndexable,
      progressBar,
      pageTitleBase,
    }: {
      preset: PresetDefinition
      pagesToAdd: PagesToAdd
      collectionId: string
      collectionName: string
      isIndexable: boolean
      progressBar: ProgressBar
      pageTitleBase?: string
    }) => {
      const state = initState()

      const schema = await collectionsApi.get(collectionId)

      const orderedPages = orderPages({ preset, pagesToAdd })

      const pagesSteps = getPagesSteps({
        orderedPages,
        collectionId,
        collectionName,
        preset,
        isFromExistingCollection: true,
        isIndexable,
        schema,
        pageTitleBase,
      })

      await progressBar.open()

      const isEditPage = preset.id === 'edit'
      try {
        await runTransactionWithRetry(editorSdkProxy, async () => {
          for (const [i, pageStep] of pagesSteps.entries()) {
            const { key, staticPageRef } = orderedPages[i]
            const pageStepTitle = getPageStepTitle({
              i18n,
              pageKey: key,
              staticPageRef,
              isEditPage,
            })
            await progressBar.update({ stepTitle: pageStepTitle })
            await pageStep.action({ state })
          }
        })

        await afterAddingPages({
          editorSdkProxy,
          collectionsApi,
          collectionId,
          pageLinkFieldsToAdd: state.pages.map(page => ({
            fieldKey: state.pageDefinitions[page.key].pageLinkFieldKey,
            pattern: state.pageDefinitions[page.key].pattern,
            routerPrefix: state.routerPrefix!,
            displayName: page.pageTitle,
            pageId: page.pageRef.id,
          })),
        })

        const label = UNDO_REDO_LABELS.ADD_DATA_BINDING_URL

        await editorSdkProxy.history.add({ label })

        biEvents.logForAddedPages({
          state,
          collectionId,
          collectionName,
          newCollection: false,
          collectionType: getCollectionType(schema),
          isEditPage,
        })

        await progressBar.update()
      } finally {
        await progressBar.close()
      }

      return {
        pageRefs: state.pages.map(p => p.pageRef),
      }
    },
    getStepsForConvertingPageFromScratch: async ({
      preset,
      pagesToAdd,
      collectionToAdd,
      isIndexable,
      pageTitleBase,
      progressBar,
    }: {
      preset: PresetDefinition
      pagesToAdd: PagesToAdd
      collectionToAdd: CollectionToAdd
      isIndexable: boolean
      pageTitleBase?: string
      progressBar: ProgressBar
    }) => {
      const { collectionId, collectionName, displayField, fields, records } =
        collectionToAdd

      const schema: Schema = {
        id: collectionId,
        displayName: collectionName,
        displayField,
        fields,
        permissions,
      }

      const pageToConvert = pagesToAdd.find(page => page.staticPageRef)

      if (!pageToConvert) {
        throw new Error('Could not find page to convert')
      }

      const hasBindings = Boolean(pageToConvert.bindings?.length)

      const orderedPages = orderPages({ preset, pagesToAdd })

      const pagesSteps = getPagesSteps({
        orderedPages,
        collectionId,
        collectionName,
        pageTitleBase,
        preset,
        isFromExistingCollection: false,
        isIndexable,
        schema,
      })

      const state = initState()

      await progressBar.open()

      try {
        await progressBar.update({
          stepTitle: getCollectionStepTitle({
            i18n,
            pagesToAdd,
            collectionName,
          }),
        })

        await createAndPopulatePresetCollection({
          collectionsApi,
          collectionId,
          collectionName,
          wixDataApi: wixData,
          schema,
          records,
          pluginsPatches,
        })

        biEvents.logForCollection({
          collectionId,
          collectionName,
          permissions,
          collectionType,
        })

        await runTransactionWithRetry(editorSdkProxy, async () => {
          for (const [i, step] of pagesSteps.entries()) {
            const { key, staticPageRef } = orderedPages[i]
            const pageStepTitle = getPageStepTitle({
              i18n,
              pageKey: key,
              staticPageRef,
            })
            await progressBar.update({ stepTitle: pageStepTitle })
            await step.action({ state })
          }
        })

        await afterAddingPages({
          editorSdkProxy,
          collectionsApi,
          collectionId,
          pageLinkFieldsToAdd: state.pages.map(page => ({
            fieldKey: state.pageDefinitions[page.key].pageLinkFieldKey,
            pattern: state.pageDefinitions[page.key].pattern,
            routerPrefix: state.routerPrefix!,
            displayName: page.pageTitle,
            pageId: page.pageRef.id,
          })),
        })

        await editorSdkProxy.history.add({
          label: UNDO_REDO_LABELS.ADD_DATA_BINDING_URL,
        })

        biEvents.logForAddedPages({
          state,
          collectionId,
          collectionName,
          newCollection: true,
          collectionType: getCollectionType(schema),
        })

        if (hasBindings) {
          // had to move to a separate transaction because otherwise it either gets stuck or is very slow
          await runTransactionWithRetry(editorSdkProxy, async () => {
            await progressBar.update({
              stepTitle: i18n.t(
                'Adding_Dynamic_Pages_Native_Progress_Bar_Connecting_Elements_Step_Title',
              ),
            })

            await bindComponents({
              editorSdkProxy,
              appApi,
              pageRef: pageToConvert.staticPageRef,
              bindings: pageToConvert.bindings,
            })
          })
          const componentIds = uniq(
            pageToConvert.bindings?.map(({ componentRef }) => componentRef.id),
          )
          appApi.emit('clearComponentCache', componentIds)
        }

        await progressBar.update()
      } finally {
        await progressBar.close()
      }

      return {
        pageRefs: state.pages.map(p => p.pageRef),
      }
    },
  }
}
